/* ============================================================
   PAGE PRO ELEMENT — catalogue filtrable
   Filtres : recherche texte, couleur, hauteur, largeur,
   profondeur, prix min-max, stock only.
   Tri : SKU / prix / hauteur.
   Add-to-cart par ligne.
   ============================================================ */

(function () {
  'use strict';

  const page = document.querySelector('.pe-page');
  if (!page) return;

  const product = window.peData && window.peData.product;
  if (!product || !product.variants) {
    console.error('[pro-element] window.peData.product absent');
    return;
  }

  const SWATCH_HEX = {
    'Gris aluminium': '#B8BCC0',
    'Blanc Jura': '#F5F0E6',
    'Gris anthracite': '#2E3133'
  };

  const formatMoney = (cents) => (cents / 100).toFixed(2).replace('.', ',') + ' €';

  // ----- Parse variants into a normalized list -----
  // Shopify raw format : variants have option1/2/3 with names in product.options.
  const optIdx = {};
  product.options.forEach((name, idx) => { optIdx[name] = idx + 1; });

  const parseFormat = (raw) => {
    // "100×47" → {largeur: 100, profondeur: 47}
    if (!raw) return { largeur: null, profondeur: null };
    const parts = raw.split(/[×x]/).map(s => parseInt(s, 10));
    return { largeur: parts[0] || null, profondeur: parts[1] || null };
  };

  const parseHeight = (raw) => parseInt(raw, 10) || 0;

  const items = product.variants.map(v => {
    const hauteur = v[`option${optIdx['Hauteur']}`] || '';
    const format  = v[`option${optIdx['Format (L×P)']}`] || '';
    const couleur = v[`option${optIdx['Couleur']}`] || '';
    const fmt = parseFormat(format);
    return {
      id: v.id,
      sku: v.sku || '',
      price: v.price,          // cents
      available: v.available !== false,
      hauteur,                 // "200 cm"
      hauteurNum: parseHeight(hauteur),
      format,                  // "100×47"
      largeur: fmt.largeur,
      profondeur: fmt.profondeur,
      couleur,
      qty: 1
    };
  });

  // ----- DOM refs -----
  const els = {
    tbody:  page.querySelector('#pe-tbody'),
    empty:  page.querySelector('#pe-empty'),
    search: page.querySelector('#pe-search'),
    priceMin: page.querySelector('#pe-price-min'),
    priceMax: page.querySelector('#pe-price-max'),
    stockOnly: page.querySelector('#pe-stock-only'),
    sort:   page.querySelector('#pe-sort'),
    reset:  page.querySelector('#pe-reset'),
    emptyReset: page.querySelector('#pe-empty-reset'),
    countFiltered: page.querySelectorAll('.pe-count-filtered'),
    countS: page.querySelector('.pe-count-s'),
    toast:  page.querySelector('#pe-toast') || document.querySelector('#pe-toast')
  };

  const filterCheckboxes = {
    couleur:    page.querySelectorAll('[data-filter="couleur"] input[type="checkbox"]'),
    hauteur:    page.querySelectorAll('[data-filter="hauteur"] input[type="checkbox"]'),
    largeur:    page.querySelectorAll('[data-filter="largeur"] input[type="checkbox"]'),
    profondeur: page.querySelectorAll('[data-filter="profondeur"] input[type="checkbox"]')
  };

  // ----- Filter state -----
  const state = {
    search: '',
    couleur: new Set(['Gris aluminium', 'Blanc Jura', 'Gris anthracite']),
    hauteur: new Set(['150 cm', '180 cm', '200 cm', '220 cm']),
    largeur: new Set([66, 100, 125]),
    profondeur: new Set([37, 47, 57]),
    priceMin: null,
    priceMax: null,
    stockOnly: false,
    sort: 'sku-asc'
  };

  // ----- Apply filters + sort -----
  const compute = () => {
    let list = items.filter(it => {
      if (state.stockOnly && !it.available) return false;
      if (!state.couleur.has(it.couleur)) return false;
      if (!state.hauteur.has(it.hauteur)) return false;
      if (it.largeur !== null && !state.largeur.has(it.largeur)) return false;
      if (it.profondeur !== null && !state.profondeur.has(it.profondeur)) return false;
      if (state.priceMin !== null && it.price < state.priceMin * 100) return false;
      if (state.priceMax !== null && it.price > state.priceMax * 100) return false;
      if (state.search) {
        const q = state.search.toLowerCase();
        const hay = `${it.sku} ${it.hauteur} ${it.format} ${it.couleur}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const [key, dir] = state.sort.split('-');
    const mul = dir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      let va, vb;
      if (key === 'sku')     { va = a.sku; vb = b.sku; return va.localeCompare(vb) * mul; }
      if (key === 'price')   { va = a.price; vb = b.price; }
      if (key === 'hauteur') { va = a.hauteurNum; vb = b.hauteurNum; }
      return (va - vb) * mul;
    });

    return list;
  };

  // ----- Render -----
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, m => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[m]));

  const renderTable = () => {
    const list = compute();
    els.countFiltered.forEach(el => el.textContent = list.length);
    if (els.countS) els.countS.style.display = list.length === 1 ? 'none' : 'inline';

    if (list.length === 0) {
      els.tbody.innerHTML = '';
      els.empty.hidden = false;
      return;
    }
    els.empty.hidden = true;

    els.tbody.innerHTML = list.map(it => {
      const dotColor = SWATCH_HEX[it.couleur] || '#ccc';
      const stockClass = it.available ? 'pe-stock-in' : 'pe-stock-out';
      const stockLabel = it.available ? 'En stock' : 'Rupture';
      const desc = `Kit de base · simple face`;
      return `
        <tr data-id="${it.id}">
          <td class="pe-col-sku">${escapeHtml(it.sku)}</td>
          <td class="pe-col-description">${desc}</td>
          <td class="pe-col-h">${escapeHtml(it.hauteur)}</td>
          <td class="pe-col-format">${escapeHtml(it.format)} cm</td>
          <td class="pe-col-couleur">
            <span class="pe-col-couleur-dot" style="background:${dotColor}"></span>
            <span>${escapeHtml(it.couleur)}</span>
          </td>
          <td class="pe-col-price">${formatMoney(it.price)}</td>
          <td class="pe-col-stock">
            <span class="pe-stock-indicator ${stockClass}">
              <span class="pe-stock-dot"></span>${stockLabel}
            </span>
          </td>
          <td class="pe-col-qty">
            <span class="pe-qty">
              <button type="button" class="pe-qty-btn" data-qty-action="dec" aria-label="Diminuer">−</button>
              <input type="number" class="pe-qty-input" min="1" max="999" value="${it.qty}">
              <button type="button" class="pe-qty-btn" data-qty-action="inc" aria-label="Augmenter">+</button>
            </span>
          </td>
          <td class="pe-col-add">
            <button type="button" class="pe-add-btn"${it.available ? '' : ' disabled'}>Panier +</button>
          </td>
        </tr>`;
    }).join('');
  };

  // ----- Events -----
  // Search
  els.search && els.search.addEventListener('input', (e) => {
    state.search = e.target.value.trim();
    renderTable();
  });

  // Checkbox filters
  Object.entries(filterCheckboxes).forEach(([key, boxes]) => {
    boxes.forEach(box => {
      box.addEventListener('change', () => {
        const val = key === 'largeur' || key === 'profondeur' ? parseInt(box.value, 10) : box.value;
        if (box.checked) state[key].add(val);
        else state[key].delete(val);
        renderTable();
      });
    });
  });

  // Price range
  const onPrice = () => {
    state.priceMin = els.priceMin.value ? parseFloat(els.priceMin.value) : null;
    state.priceMax = els.priceMax.value ? parseFloat(els.priceMax.value) : null;
    renderTable();
  };
  els.priceMin && els.priceMin.addEventListener('input', onPrice);
  els.priceMax && els.priceMax.addEventListener('input', onPrice);

  // Stock
  els.stockOnly && els.stockOnly.addEventListener('change', (e) => {
    state.stockOnly = e.target.checked;
    renderTable();
  });

  // Sort
  els.sort && els.sort.addEventListener('change', (e) => {
    state.sort = e.target.value;
    renderTable();
  });

  // Reset filters
  const resetFilters = () => {
    state.search = '';
    if (els.search) els.search.value = '';
    state.priceMin = null; state.priceMax = null;
    if (els.priceMin) els.priceMin.value = '';
    if (els.priceMax) els.priceMax.value = '';
    state.stockOnly = false;
    if (els.stockOnly) els.stockOnly.checked = false;
    state.sort = 'sku-asc';
    if (els.sort) els.sort.value = 'sku-asc';
    state.couleur = new Set(['Gris aluminium', 'Blanc Jura', 'Gris anthracite']);
    state.hauteur = new Set(['150 cm', '180 cm', '200 cm', '220 cm']);
    state.largeur = new Set([66, 100, 125]);
    state.profondeur = new Set([37, 47, 57]);
    Object.values(filterCheckboxes).forEach(boxes => boxes.forEach(b => b.checked = true));
    renderTable();
  };
  els.reset && els.reset.addEventListener('click', resetFilters);
  els.emptyReset && els.emptyReset.addEventListener('click', resetFilters);

  // Qty + Add to cart — event delegation sur tbody
  els.tbody.addEventListener('click', async (e) => {
    const tr = e.target.closest('tr[data-id]');
    if (!tr) return;
    const id = parseInt(tr.dataset.id, 10);
    const item = items.find(i => i.id === id);
    if (!item) return;

    const qtyBtn = e.target.closest('[data-qty-action]');
    const addBtn = e.target.closest('.pe-add-btn');
    const input = tr.querySelector('.pe-qty-input');

    if (qtyBtn) {
      const cur = parseInt(input.value, 10) || 1;
      if (qtyBtn.dataset.qtyAction === 'inc') input.value = Math.min(999, cur + 1);
      if (qtyBtn.dataset.qtyAction === 'dec') input.value = Math.max(1, cur - 1);
      item.qty = parseInt(input.value, 10);
      return;
    }

    if (addBtn) {
      const qty = Math.max(1, parseInt(input.value, 10) || 1);
      item.qty = qty;
      addBtn.textContent = 'Ajout…';
      addBtn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: qty }] })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Erreur ajout panier');
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        addBtn.textContent = '✓ Ajouté';
        addBtn.classList.add('is-success');
        showToast(`${item.sku} × ${qty} ajouté au panier`);
        setTimeout(() => {
          addBtn.textContent = 'Panier +';
          addBtn.classList.remove('is-success');
          addBtn.disabled = false;
        }, 1500);
      } catch (err) {
        console.error(err);
        showToast('Erreur : ' + err.message, true);
        addBtn.textContent = 'Panier +';
        addBtn.disabled = false;
      }
    }
  });

  // Input direct
  els.tbody.addEventListener('input', (e) => {
    if (!e.target.classList.contains('pe-qty-input')) return;
    const tr = e.target.closest('tr[data-id]');
    if (!tr) return;
    const id = parseInt(tr.dataset.id, 10);
    const item = items.find(i => i.id === id);
    if (item) item.qty = Math.max(1, parseInt(e.target.value, 10) || 1);
  });

  // Toast
  const showToast = (msg, isError = false) => {
    if (!els.toast) return;
    els.toast.textContent = msg;
    els.toast.classList.toggle('is-error', isError);
    els.toast.classList.add('is-visible');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => els.toast.classList.remove('is-visible'), 2500);
  };

  // Init
  renderTable();
})();
